// Schémas Zod de la section « Recettes & achats » (27/10/2026).
//
// Trois sources alimentent les deux registres, sans double saisie :
//   • LIVRE DES RECETTES = factures payées (date d'encaissement + mode de règlement, saisis
//     au clic « Marquer payée ») ;
//   • REGISTRE DES ACHATS = entrées de stock portant un montant payé (achats d'intrants, déjà
//     saisis à la réception) + FRAIS hors articles (table `frais`, ci-dessous).
//
// Règle du registre des achats (micro-entrepreneur, vente de marchandises) : chronologique,
// distinguer les espèces des autres règlements, référence des pièces justificatives.
// Les listes sont du TEXTE en base (aucun enum SQL) : les étendre ne demande pas de migration.

import { z } from 'zod'

// --- Mode de règlement (partagé : factures, entrées de stock, frais) ---------------------
export const MODES_REGLEMENT = [
  'especes',
  'carte',
  'cheque',
  'virement',
  'prelevement',
  'autre',
] as const
export type ModeReglement = (typeof MODES_REGLEMENT)[number]

export const LIBELLES_MODE_REGLEMENT: Record<ModeReglement, string> = {
  especes: 'Espèces',
  carte: 'Carte bancaire',
  cheque: 'Chèque',
  virement: 'Virement',
  prelevement: 'Prélèvement',
  autre: 'Autre',
}

export const modeReglementSchema = z.enum(MODES_REGLEMENT)

// Date au format AAAA-MM-JJ (même règle que les autres modules).
export const dateComptaSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ.')

// Montant en euros : positif, deux décimales au plus (arrondi côté service).
export const montantSchema = z
  .number({ invalid_type_error: 'Montant invalide.' })
  .min(0, 'Le montant ne peut pas être négatif.')
  .max(1_000_000, 'Montant trop grand.')

// --- Frais hors articles ------------------------------------------------------------
export const CATEGORIES_FRAIS = [
  'emplacement',
  'commissions',
  'assurance',
  'abonnements',
  'deplacements',
  'communication',
  'petit_materiel',
  'formation',
  'autre',
] as const
export type CategorieFrais = (typeof CATEGORIES_FRAIS)[number]

export const LIBELLES_CATEGORIE_FRAIS: Record<CategorieFrais, string> = {
  emplacement: 'Marchés, salons, droits de place',
  commissions: 'Commissions bancaires et terminal de paiement',
  assurance: 'Assurances',
  abonnements: 'Abonnements et logiciels',
  deplacements: 'Déplacements',
  communication: 'Publicité et communication',
  petit_materiel: 'Petit matériel et fournitures',
  formation: 'Formation',
  autre: 'Autre',
}

export const fraisCreateSchema = z.object({
  dateFrais: dateComptaSchema,
  libelle: z
    .string()
    .trim()
    .min(1, 'Le libellé est obligatoire.')
    .max(200, 'Libellé trop long (200 caractères max).'),
  categorie: z.enum(CATEGORIES_FRAIS).default('autre'),
  montant: montantSchema.refine((n) => n > 0, 'Le montant doit être supérieur à 0.'),
  modeReglement: modeReglementSchema.nullable().optional(),
  fournisseur: z.string().trim().max(200, 'Nom trop long (200 caractères max).').nullable().optional(),
  reference: z.string().trim().max(80, 'Référence trop longue (80 caractères max).').nullable().optional(),
  note: z.string().trim().max(300, 'Note trop longue (300 caractères max).').nullable().optional(),
})
export type FraisCreateInput = z.infer<typeof fraisCreateSchema>

// Mise à jour : mêmes champs, tous facultatifs.
export const fraisUpdateSchema = fraisCreateSchema.partial()
export type FraisUpdateInput = z.infer<typeof fraisUpdateSchema>

// Période d'un registre : bornes incluses, AAAA-MM-JJ.
export const periodeSchema = z
  .object({ debut: dateComptaSchema, fin: dateComptaSchema })
  .refine((p) => p.debut <= p.fin, 'La date de début doit précéder la date de fin.')

// --- Import d'un relevé de terminal de paiement (migration 0037) ---------------------
// Le fichier est LU DANS LE NAVIGATEUR (src/lib/import-terminal/) : seul ce relevé
// normalisé est envoyé au serveur. Un « adaptateur » par terminal ; pour en ajouter un
// (SumUp…), on ajoute sa clé ici et son adaptateur côté navigateur.
export const TERMINAUX = ['paypal_pos'] as const
export type Terminal = (typeof TERMINAUX)[number]

export const LIBELLES_TERMINAL: Record<Terminal, string> = {
  paypal_pos: 'PayPal POS',
}

const ligneReleveSchema = z.object({
  libelle: z.string().trim().min(1).max(200),
  montant: montantSchema,
})

export const releveTerminalSchema = z
  .object({
    terminal: z.enum(TERMINAUX),
    periodeDebut: dateComptaSchema,
    periodeFin: dateComptaSchema,
    nomFichier: z.string().trim().max(200).nullable().optional(),
    totalVentesTtc: montantSchema,
    totalVentesHt: montantSchema,
    encaissements: z
      .array(ligneReleveSchema.extend({ modeReglement: modeReglementSchema }))
      .min(1, 'Le relevé ne contient aucun paiement.')
      .max(20),
    frais: z.array(ligneReleveSchema).max(20),
  })
  .refine((r) => r.periodeDebut <= r.periodeFin, 'La période du relevé est invalide.')
export type ReleveTerminal = z.infer<typeof releveTerminalSchema>
